// Environment variables the SPA cannot boot without. Missing values are
// surfaced loudly at startup (see main.tsx) instead of failing cryptically
// deep in auth or API calls.
//
// VITE_WS_URL is intentionally NOT here: usePeerCursors.ts (its one consumer)
// falls back to deriving it from VITE_API_URL when unset, so a deploy that
// sets only the REST API URL (or only the game socket var, VITE_GAME_WS_URL)
// still boots.
//
// VITE_CAPTCHA_SITE_KEY (the publish and wiki Soulstack Verify key, see
// services/captcha.ts) is required in a production build: scripts/deploy.sh was
// the only place it was ever set, so a CI deploy silently shipped an app whose
// publish and wiki gate never asked for a token. In development it stays
// optional, matching the API's empty-secret state. The sign in, sign up, reset,
// contact and guest signup keys are constants in code, not env vars.
export const REQUIRED_VARS = [
  'VITE_API_URL',
  'VITE_COGNITO_USER_POOL_ID',
  'VITE_COGNITO_CLIENT_ID',
  'VITE_CAPTCHA_SITE_KEY',
] as const;

// A dev build without a configured Cognito pool intentionally falls back to
// AuthProvider's local dev identity (mirrors the API's dev-tenant bypass), so
// only the API URL is truly required to boot locally. Requiring the Cognito
// vars here too would make that documented dev workflow unreachable.
export const REQUIRED_VARS_DEV = ['VITE_API_URL'] as const;

export type RequiredVar = (typeof REQUIRED_VARS)[number];

/** Return the required vars that are absent or empty in the given env bag. */
export function findMissingEnv(env: Record<string, unknown>): RequiredVar[] {
  const vars: readonly RequiredVar[] = env.DEV ? REQUIRED_VARS_DEV : REQUIRED_VARS;
  return vars.filter((name) => !env[name]);
}
